import { useCallback, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { RequestStatus } from 'core/domain/Request'

import { type Shipper } from '../domain/shippers'
import { useShippers } from '../hooks/useShippers'
import { EmptyShippersListState } from './EmptyState'
import { ShipperDetailDrawer } from './ShipperDetailDrawer'
import { ShipperListTable } from './ShipperListTable'

type ShipperListParams = {
  page: number
  pageSize: number
  search: string
}

const initialRequestParams: ShipperListParams = {
  page: 0,
  pageSize: 10,
  search: '',
}

export function ShipperList() {
  const [params, setParams] = useState<ShipperListParams>(initialRequestParams)
  const [searchInputValue, setSearchInputValue] = useState('')
  const [selectedShipperUUID, setSelectedShipperUUID] = useState<string | undefined>(undefined)

  const { page, pageSize, search } = params
  const offset = page * pageSize
  const { shippers, totalCount, status } = useShippers({ limit: pageSize, offset, search })

  const handlePageChange = useCallback((newPage: number) => {
    setParams(prev => ({ ...prev, page: newPage }))
  }, [])

  const handleRowClick = useCallback((shipper: Shipper) => {
    setSelectedShipperUUID(shipper.id)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setSelectedShipperUUID(undefined)
  }, [])

  const updateSearchParams = useDebouncedCallback((searchTerm: string) => {
    setParams(prev => ({ ...prev, search: searchTerm, page: 0 }))
  }, 500)

  const handleSearchChange = useCallback(
    (searchTerm: string) => {
      setSearchInputValue(searchTerm)
      updateSearchParams(searchTerm)
    },
    [updateSearchParams]
  )

  const isLoading = status === RequestStatus.Pending || status === RequestStatus.Initial

  // Show empty state only when request is done, no search filter, and no results
  const isInitialEmptyState = status === RequestStatus.Done && !search && totalCount === 0
  const isError = status === RequestStatus.Error

  if (isInitialEmptyState) {
    return <EmptyShippersListState />
  }

  return (
    <>
      <ShipperListTable
        shippers={shippers}
        isLoading={isLoading}
        isError={isError}
        totalCount={totalCount}
        currentPage={page}
        pageSize={pageSize}
        searchQuery={searchInputValue}
        onPageChange={handlePageChange}
        onSearchChange={handleSearchChange}
        onRowClick={handleRowClick}
      />
      <ShipperDetailDrawer
        shipperUUID={selectedShipperUUID}
        isOpen={!!selectedShipperUUID}
        onClose={handleDrawerClose}
      />
    </>
  )
}
